/**
 * HFA Habit Adoption Assessment — Scoring Engine v3
 *
 * Formulas per Clutch_Assessment_Platform_Build_Guide_v5D:
 *
 * Dimension score:
 *   - No reverse-coded items
 *   - avg of all items in dimension
 *   - score_pct = Math.round((avg − 1) / 4 × 100)
 *
 * Habit Mechanics Score (leading):
 *   - simple average of EE, PR, CT, RF scores
 *
 * Adoption Outcomes Score (lagging):
 *   - BO score directly
 *
 * HFA Index (composite):
 *   - 0.60 × Outcomes + 0.40 × Mechanics
 *
 * Scoring version constant: "v3.0"
 */

import type { HfaScoreOutput, ResponseMap, ScoreBand } from '../types'
import { HFA_ITEMS, HFA_DIMS } from '../data/hfa-items'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pctScore(values: (number | null)[]): number | null {
  const filled = values.filter((v): v is number => v !== null)
  if (filled.length === 0) return null
  const avg = filled.reduce((s, v) => s + v, 0) / filled.length
  return Math.round(((avg - 1) / 4) * 100)
}

export function getBand(score: number | null): ScoreBand {
  if (score === null) return null
  if (score >= 75) return 'strong'
  if (score >= 50) return 'developing'
  return 'needs'
}

export function getBandLabel(band: ScoreBand): string {
  switch (band) {
    case 'strong':     return 'Strong'
    case 'developing': return 'Developing'
    case 'needs':      return 'Needs Support'
    default:           return '—'
  }
}

// ─── Main scoring function ────────────────────────────────────────────────────

export function scoreHfa(responses: ResponseMap): HfaScoreOutput {
  const dimScores: HfaScoreOutput['dims'] = {} as HfaScoreOutput['dims']
  const mechanicsDims = ['ee', 'pr', 'ct', 'rf'] as const

  for (const dimMeta of HFA_DIMS) {
    const dimItems = HFA_ITEMS.filter(item => item.dim === dimMeta.key)
    const vals = dimItems.map(item => responses[item.id] ?? null)
    const score = pctScore(vals)

    dimScores[dimMeta.key] = {
      score,
      band: getBand(score),
    }
  }

  // Mechanics: average of EE, PR, CT, RF
  const mechVals = mechanicsDims
    .map(d => dimScores[d].score)
    .filter((v): v is number => v !== null)

  const mechanicsScore =
    mechVals.length > 0
      ? Math.round(mechVals.reduce((a, b) => a + b, 0) / mechVals.length)
      : null

  // Outcomes = BO score directly
  const outcomesScore = dimScores.bo.score

  // HFA Index: 60% Outcomes + 40% Mechanics
  let habitAdoptionIndex: number | null = null
  if (mechanicsScore !== null && outcomesScore !== null) {
    habitAdoptionIndex = Math.round(0.6 * outcomesScore + 0.4 * mechanicsScore)
  } else if (outcomesScore !== null) {
    habitAdoptionIndex = Math.round(0.6 * outcomesScore)
  } else if (mechanicsScore !== null) {
    habitAdoptionIndex = Math.round(0.4 * mechanicsScore)
  }

  return {
    dims: dimScores,
    mechanicsScore,
    outcomesScore,
    habitAdoptionIndex,
    indexBand: getBand(habitAdoptionIndex),
    version: 'v3.0',
  }
}

// ─── Count helpers ────────────────────────────────────────────────────────────

export function countAnswered(responses: ResponseMap): number {
  return HFA_ITEMS.filter(item => responses[item.id] != null).length
}

export const HFA_TOTAL_ITEMS = HFA_ITEMS.length  // 17
